import React, { createContext, useContext, ReactNode } from "react";

/**
 * Menu position type
 * - expanded: Menu is permanently expanded
 * - collapsed: Menu is permanently collapsed (can temporarily expand on hover)
 */
export type MenuPosition = "expanded" | "collapsed";

/**
 * Context value for MainMenu state
 */
interface MainMenuContextValue {
  /**
   * Whether the main menu is in panel (expanded) mode
   * - true: expanded (264px)
   * - false: collapsed (52px)
   * Note: This can be temporary (hover) or permanent (user preference)
   */
  isPanel: boolean;

  /**
   * Permanent menu position from user preference
   * - "expanded": Menu is always expanded
   * - "collapsed": Menu is collapsed but can expand on hover
   */
  menuPosition: MenuPosition;

  /**
   * Current width of the main menu in pixels
   */
  width: number;

  /**
   * Whether the main menu is collapsed (opposite of isPanel)
   */
  isCollapsed: boolean;

  /**
   * Whether the menu is temporarily expanded (hover state)
   * - true: Menu is collapsed but currently expanded due to hover
   * - false: Menu is in its permanent state
   */
  isTemporaryExpanded: boolean;
}

const MainMenuContext = createContext<MainMenuContextValue>({
  isPanel: true,
  menuPosition: "expanded",
  width: 264,
  isCollapsed: false,
  isTemporaryExpanded: false,
});

/**
 * Hook to access MainMenu state
 * @returns MainMenu context value
 */
export const useMainMenu = () => useContext(MainMenuContext);

/**
 * Provider component for MainMenu state
 */

export const collapsedMenuWidth = 52;
export const expandedMenuWidth = 264;

export const MainMenuProvider: React.FC<{
  isPanel: boolean;
  menuPosition: MenuPosition;
  children: ReactNode;
}> = ({ isPanel, menuPosition, children }) => {
  const width = isPanel ? expandedMenuWidth : collapsedMenuWidth;

  // Determine if menu is temporarily expanded (hover state)
  // When menuPosition is "collapsed" but isPanel is true, it means user is hovering
  const isTemporaryExpanded = menuPosition === "collapsed" && isPanel;

  const value: MainMenuContextValue = {
    isPanel,
    menuPosition,
    width,
    isCollapsed: !isPanel,
    isTemporaryExpanded,
  };

  return (
    <MainMenuContext.Provider value={value}>
      {children}
    </MainMenuContext.Provider>
  );
};
